import { useState } from "react";
import { MapPin } from "lucide-react";
import { useLang } from "@/lib/i18n";

export default function OfficeMap({ offices }) {
  const { t, L } = useLang();
  const list = offices.filter((o) => o.map_query);
  const [activeId, setActiveId] = useState(list[0]?.id);
  const active = list.find((o) => o.id === activeId) || list[0];

  if (!active) return null;

  return (
    <div data-testid="office-map-block">
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div>
          <p className="eyebrow mb-3">{t("contact_offices_label")}</p>
          <h2 className="font-head text-3xl md:text-4xl">{t("contact_map_title")}</h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="office-map-switcher">
          {list.map((o) => (
            <button
              key={o.id}
              type="button"
              data-testid={`office-map-tab-${o.id}`}
              onClick={() => setActiveId(o.id)}
              className={`h-9 rounded-full px-4 text-xs font-semibold tracking-wide transition-colors ${
                active.id === o.id ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70 hover:text-primary"
              }`}
            >
              {L(o.city) || L(o.country)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-4 flex flex-col justify-center border border-border bg-card/40 p-8" style={{ borderRadius: "var(--radius)" }}>
          <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <MapPin className="h-5 w-5" />
          </span>
          <p className="eyebrow mb-3">{L(active.kind) || t("rep_badge")}</p>
          <h3 className="font-head text-2xl md:text-3xl" data-testid="office-map-active-city">{L(active.city) || L(active.country)}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{L(active.country)}</p>
          {L(active.address) && <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{L(active.address)}</p>}
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(active.map_query)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="office-map-directions"
            className="btn-outline mt-7 h-10 px-5 text-xs"
          >
            {t("contact_directions")}
          </a>
        </div>
        <div className="lg:col-span-8 overflow-hidden border border-border" style={{ borderRadius: "var(--radius)" }}>
          <iframe
            key={active.id}
            title={`Map — ${L(active.city)}`}
            data-testid="office-map-iframe"
            src={`https://www.google.com/maps?q=${encodeURIComponent(active.map_query)}&output=embed`}
            className="map-frame h-[300px] w-full md:h-[420px]"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}
